/**
 * Drives the live internet AIS source (AISStream.io) when it's selected and AIS
 * is on. Resolves the transport for the current runtime (DIRECT to AISStream in
 * the packaged app, the same-origin dev proxy in the browser), feeds
 * PositionReport/ShipStaticData into the shared target store (merging static data
 * onto the moving target), updates the bounding-box subscription as the chart
 * moves (debounced), and ages out vessels that stop reporting. Real vessels at
 * real positions, so it's meaningful with device GPS at Tobermory, not over the
 * fictional training area.
 */
import { useEffect } from 'react';
import { useAisStore } from '@/store/ais';
import { useChartSourcesStore } from '@/store/chartSources';
import { useChartPlotterStore } from '@/store';
import { connectAisStream, resolveAisTransport, aisSubscriptionBox, mergeAisTarget, type Bbox } from '@/ais/aisstream';

const TOBERMORY: [number, number] = [-6.064, 56.622];

export function AisInternetRunner() {
  const source = useAisStore((s) => s.source);
  const enabled = useAisStore((s) => s.enabled);
  const apiKey = useAisStore((s) => s.apiKey);

  useEffect(() => {
    if (source !== 'internet' || !enabled) return;
    const transport = resolveAisTransport(apiKey);
    if (transport.mode === 'nokey') {
      useAisStore.getState().setConn('nokey');
      return;
    }
    const bbox = (): Bbox => {
      const vb = useChartSourcesStore.getState().viewBounds;
      const boat = useChartPlotterStore.getState().ownship.position ?? null;
      // Follow the chart view when we have it, else the boat, but never subscribe
      // to a patch so small that nearby ships never arrive (a too-small box is a
      // common cause of an empty "no ships" that isn't really empty).
      return aisSubscriptionBox(vb, boat, TOBERMORY);
    };
    const handle = connectAisStream(
      transport,
      bbox(),
      (partial) => {
        // Store any vessel with a valid position; a missing course/speed is
        // "unknown", not a reason to drop it (that gate is what emptied the plot).
        const cur = useAisStore.getState().targets[partial.mmsi];
        const merged = mergeAisTarget(cur, partial);
        if (merged) useAisStore.getState().upsert(merged);
      },
      (st, msg) => useAisStore.getState().setConn(st, msg),
      // Let the stall watchdog see how many targets we're holding, so it only
      // reconnects a silent feed when there are contacts going stale — genuinely
      // empty water stays quiet rather than flapping through a false reconnect.
      () => Object.keys(useAisStore.getState().targets).length,
    );
    // Resubscribe to the current view as the chart pans/zooms (debounced).
    let deb: number | undefined;
    let lastVb = useChartSourcesStore.getState().viewBounds;
    const unsub = useChartSourcesStore.subscribe((state) => {
      if (state.viewBounds !== lastVb) {
        lastVb = state.viewBounds;
        window.clearTimeout(deb);
        deb = window.setTimeout(() => handle.updateBbox(bbox()), 1500);
      }
    });
    // Age out targets not heard from in 10 minutes.
    const ageId = window.setInterval(() => useAisStore.getState().prune(Date.now() - 10 * 60_000), 30_000);
    return () => {
      handle.close();
      unsub();
      window.clearTimeout(deb);
      window.clearInterval(ageId);
      useAisStore.getState().setConn('idle');
    };
  }, [source, enabled, apiKey]);
  return null;
}
